import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCode, faCopy } from "@fortawesome/pro-light-svg-icons";
import { toast } from "../../../../utils/demoToast";
import Form from "../../../inputs/Form";
import Button from "../../../buttons/Button";
import Modal from "../../../utils/Modal";
import { copyToClipboard } from "../../../../utils/download";
import { getEnvVariable } from "../../../../utils/config";
import {
  updateDefaultBookingDuration,
  updateAutoAcceptBookings,
} from "../../../../services/tableService";
import { getRequest } from "../../../../api/methodsCfg";

const Service = ({ tKey = "dashboard.settings.service." }) => {
  const { t } = useTranslation();
  const restaurantId = useSelector((state) => state.user.restaurantId);
  const controlledRestaurantId = useSelector(
    (state) => state.admin.controlledRestaurantId,
  );

  const activeRestaurantId = controlledRestaurantId || restaurantId;

  const [defaultDuration, setDefaultDuration] = useState(2);
  const [autoAcceptBookings, setAutoAcceptBookings] = useState(true);
  const [isWidgetModalOpen, setIsWidgetModalOpen] = useState(false);

  // Fetch restaurant settings on mount
  useEffect(() => {
    if (!activeRestaurantId) return;
    const fetchSettings = async () => {
      try {
        const response = await getRequest(
          `/restaurant/${activeRestaurantId}`,
        );
        if (response.data?.defaultBookingDuration != null) {
          setDefaultDuration(Number(response.data.defaultBookingDuration));
        }
        if (response.data?.enableAutoAcceptBookings != null) {
          setAutoAcceptBookings(response.data.enableAutoAcceptBookings);
        }
      } catch (err) {
        console.warn("Could not fetch restaurant settings:", err);
      }
    };
    fetchSettings();
  }, [activeRestaurantId]);

  const handleDurationChange = async (duration) => {
    try {
      await updateDefaultBookingDuration(activeRestaurantId, duration);
      setDefaultDuration(duration);
      toast.success(t(`${tKey}success.durationUpdated`));
    } catch (error) {
      console.error("Error updating default booking duration:", error);
      toast.error(t(`${tKey}errors.updateDuration`));
    }
  };

  const handleAutoAcceptChange = async (enabled) => {
    try {
      await updateAutoAcceptBookings(activeRestaurantId, enabled);
      setAutoAcceptBookings(enabled);
      toast.success(t(`${tKey}success.autoAcceptUpdated`));
    } catch (error) {
      console.error("Error updating auto-accept bookings:", error);
      toast.error(t(`${tKey}errors.updateAutoAccept`));
    }
  };

  const bookingSettingsFields = useMemo(
    () => [
      {
        name: "defaultBookingDuration",
        label: t(`${tKey}defaultBookingDuration`),
        type: "dropdown",
        class: "w-100",
        options: [
          { value: 1, label: `1 ${t(`${tKey}hour`)}` },
          { value: 1.5, label: `1h30` },
          { value: 2, label: `2 ${t(`${tKey}hours`)}` },
          { value: 2.5, label: `2h30` },
          { value: 3, label: `3 ${t(`${tKey}hours`)}` },
        ],
      },
      {
        name: "enableAutoAcceptBookings",
        label: t(`${tKey}enableAutoAcceptBookings.label`),
        info: t(`${tKey}enableAutoAcceptBookings.info`),
        type: "checkbox",
        class: "w-100",
      },
    ],
    [t, tKey],
  );

  const bookingSettingsFormValue = useMemo(
    () => ({
      defaultBookingDuration: {
        value: defaultDuration,
        label:
          defaultDuration === 1
            ? `1 ${t(`${tKey}hour`)}`
            : defaultDuration % 1 !== 0
              ? `${Math.floor(defaultDuration)}h30`
              : `${defaultDuration} ${t(`${tKey}hours`)}`,
      },
      enableAutoAcceptBookings: autoAcceptBookings,
    }),
    [defaultDuration, autoAcceptBookings, t, tKey],
  );

  return (
    <div className="dashboard-content flex gap-2" style={{ height: "100%", minHeight: "100%" }}>
      <div className="w-50 bg-white rounded p-3">
        <Form
          formGroupClass="mb-4"
          fields={bookingSettingsFields}
          currentForm={bookingSettingsFormValue}
          setFormDetails={(newForm) => {
            // Handle duration change
            const opt = newForm?.defaultBookingDuration;
            const num =
              opt?.value != null && typeof opt.value === "object"
                ? opt.value.value
                : opt?.value;
            if (num != null && num !== defaultDuration) {
              handleDurationChange(Number(num));
            }
            // Handle auto-accept change
            const enabled = newForm?.enableAutoAcceptBookings;
            if (enabled !== autoAcceptBookings) {
              handleAutoAcceptChange(enabled);
            }
          }}
          setIsValid={() => {}}
          fieldClass="bg-pastel-light mb-0"
        />
      </div>
      <div className="w-50 bg-white rounded p-3 flex align-center justify-center">
        <Button
          variant="info"
          onClick={() => setIsWidgetModalOpen(true)}
        >
          <FontAwesomeIcon icon={faCode} className="mr-1" />
          {t(`${tKey}widgetEmbed.button`)}
        </Button>
      </div>

      {/* Widget Embed Modal */}
      <Modal
        isOpen={isWidgetModalOpen}
        onClose={() => setIsWidgetModalOpen(false)}
        noFooter
      >
        <div className="p-3">
          <h3 className="mt-0 mb-3">
            <FontAwesomeIcon icon={faCode} className="mr-2" />
            {t(`${tKey}widgetEmbed.title`)}
          </h3>

          <div className="mb-4">
            <h4 className="mb-2">{t(`${tKey}widgetEmbed.steps.title`)}</h4>
            <ol className="pl-3 text-sm" style={{ lineHeight: 1.8 }}>
              <li>{t(`${tKey}widgetEmbed.steps.step1`)}</li>
              <li>{t(`${tKey}widgetEmbed.steps.step2`)}</li>
              <li>{t(`${tKey}widgetEmbed.steps.step3`)}</li>
              <li>{t(`${tKey}widgetEmbed.steps.step4`)}</li>
            </ol>
          </div>

          <div className="mb-3">
            <h4 className="mb-2">{t(`${tKey}widgetEmbed.scriptTitle`)}</h4>
            <div className="position-relative">
              <pre
                className="p-3 rounded mb-0"
                style={{
                  backgroundColor: "#2d2d2d",
                  color: "#f8f8f2",
                  fontSize: "13px",
                  overflow: "auto",
                }}
              >
                {`<script
  src="${getEnvVariable("REACT_APP_CDN_BASE_URL")}/bookings.js"
  data-restaurant-id="${activeRestaurantId}"
></script>`}
              </pre>
              <Button
                variant="info"
                size="xs"
                className="position-absolute"
                style={{ top: "8px", right: "8px" }}
                onClick={() => {
                  const script = `<script\n  src="${getEnvVariable("REACT_APP_CDN_BASE_URL")}/bookings.js"\n  data-restaurant-id="${activeRestaurantId}"\n></script>`;
                  copyToClipboard(script, t(`${tKey}widgetEmbed.copied`));
                }}
                title={t(`${tKey}widgetEmbed.copy`)}
              >
                <FontAwesomeIcon icon={faCopy} className="mr-1" />
                {t(`${tKey}widgetEmbed.copy`)}
              </Button>
            </div>
          </div>

          <div className="flex justify-between align-center mt-3">
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                const subject = encodeURIComponent(
                  t(`${tKey}widgetEmbed.helpEmailSubject`),
                );
                const body = encodeURIComponent(
                  t(`${tKey}widgetEmbed.helpEmailBody`, {
                    restaurantId: activeRestaurantId,
                  }),
                );
                window.open(
                  `mailto:info@offood.ch?subject=${subject}&body=${body}`,
                  "_blank",
                );
              }}
            >
              {t(`${tKey}widgetEmbed.requestHelp`)}
            </Button>
            <Button
              variant="primary"
              onClick={() => setIsWidgetModalOpen(false)}
            >
              {t("utils.common.close")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Service;
